import Seo from "@/components/Seo";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { ABOUT_IMAGE } from "@/data/siteData";
import { Scale, Compass, Handshake } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const APPROACH_ICONS = [Scale, Compass, Handshake];

const TEAM = [
  { name: "Nishit Chhajed", role: "Director & Principal Officer", photo: "/team/nishit.jpg", quals: "B.Tech, FIII", bio: "Leads Wisdom's broking practice — policy auditing, claims consultancy and client advisory." },
  { name: "Nehal Jain", role: "Chief Financial Officer", photo: "/team/nehal.jpg", quals: "CA, AIII", bio: "Heads finance and compliance — keeping the firm's books, audits and statutory reporting in order." },
  { name: "Mohit Aggarwal", role: "Senior Vice President – Insurance Business", photo: "/team/mohit.jpg", bio: "Drives the insurance business across commercial lines and key client relationships." },
  { name: "Sunil Kalekar", role: "Head – Life Insurance", photo: "/team/sunil.jpg", quals: "B.Com, LIII", bio: "Heads the life insurance practice — term and group life covers for individuals and employers." },
  { name: "Chaitali Patil", role: "Senior Manager – Operations", photo: "/team/chaitali.jpg", quals: "B.Tech", bio: "Runs day-to-day operations, keeping policies, endorsements and renewals on track." },
  { name: "Shalini Lata", role: "Senior Manager – Client Servicing", photo: "/team/shalini.jpg", quals: "B.Com, MBA – Finance", bio: "First point of contact for clients — servicing, documentation and follow-ups." },
  { name: "Jitendra Kakde", role: "Office & Collection Executive", photo: "/team/jitendra.jpg", bio: "Handles office coordination and premium collections." },
  { name: "Imran Shaikh", role: "Senior Executive – Renewals & Operations", photo: "/team/imran.jpg", quals: "B.Com", bio: "Keeps renewals on time and supports operations across client accounts." },
];

export default function AboutUs() {
  const { t } = useLanguage();
  return (
    <div data-testid="about-us-page">
      <Seo
        title="About Us | Wisdom Insurance Broking Private Limited, Pune"
        description="Wisdom Insurance Broking Private Limited is an IRDAI-licensed insurance broker based in Pune, specialising in commercial insurance for businesses, contractors, manufacturers and financial institutions."
        keywords="insurance broker Pune, IRDAI licensed broker Maharashtra, commercial insurance advisory"
      />

      <section className="grain bg-navy-deep pb-16 pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow light>{t("ab.eyebrow")}</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              {t("ab.title")}
            </h1>
          </FadeIn>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8 md:py-24 lg:px-16">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
          <FadeIn className="lg:col-span-7">
            <Eyebrow>{t("ab.whoEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">
              {t("ab.whoTitle")}
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-600">
              <p>{t("ab.p1")}</p>
              <p>{t("ab.p2")}</p>
              <p>{t("ab.p3")}</p>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -right-3 -top-3 h-full w-full border border-amber-500/40" aria-hidden="true" />
              <img src={ABOUT_IMAGE} alt="Pune business district" className="relative h-80 w-full object-cover" loading="lazy" />
            </div>
          </FadeIn>
        </div>

        <div className="mt-20">
          <FadeIn>
            <Eyebrow>{t("ab.apEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">{t("ab.apTitle")}</h2>
          </FadeIn>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {APPROACH_ICONS.map((Icon, i) => (
              <FadeIn key={i} delay={i * 0.08}>
                <div data-testid={`approach-${i + 1}`} className="h-full border border-slate-200 bg-white p-8">
                  <Icon className="h-7 w-7 text-amber-600" strokeWidth={1.5} />
                  <h3 className="mt-5 font-heading text-lg font-semibold text-navy">{t(`ab.ap${i + 1}t`)}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{t(`ab.ap${i + 1}x`)}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>

        <div className="mt-20">
          <FadeIn>
            <Eyebrow>{t("ab.lEyebrow")}</Eyebrow>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">{t("ab.lTitle")}</h2>
          </FadeIn>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {TEAM.map((m, i) => (
              <FadeIn key={m.name} delay={i * 0.08}>
                <div data-testid={`leader-card-${i + 1}`} className="h-full overflow-hidden border border-slate-200 bg-white">
                  {m.photo ? (
                    <img src={m.photo} alt={m.name} className="aspect-[4/5] w-full object-cover object-top" loading="lazy" />
                  ) : (
                    <div className="flex aspect-[4/5] w-full items-center justify-center border-b border-dashed border-slate-300 bg-slate-50 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                      {t("ab.photo")}
                    </div>
                  )}
                  <div className="p-5">
                    <h3 className="font-heading text-base font-semibold text-navy">{m.name}</h3>
                    {m.quals && <p className="mt-0.5 font-mono text-[11px] tracking-wider text-slate-500">{m.quals}</p>}
                    <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-amber-700">{m.role}</p>
                    {m.bio && <p className="mt-2.5 text-xs leading-relaxed text-slate-500">{m.bio}</p>}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
